/**
 * screens/settings/customerAccess.tsx — N-1 · Customer access.
 *
 * SKPLATFORM-UICC-006 §6 / DB-006 S7a.
 *
 * ── READ THIS BEFORE CHANGING ANYTHING ON THIS SCREEN ─────────────────────
 *
 * There are now two large numbers in the product that look alike and mean
 * completely different things:
 *
 *   SEATS     — named staff principals an administrator provisioned, billed
 *               per seat, counted from identity.users + roles.
 *   CUSTOMERS — people who signed in or transacted this period, metered per
 *               block of 1,000, counted from activity and holding no row in
 *               any principal table.
 *
 * An operator with 40 staff and 12,400 active riders who reads "12,440 users"
 * will reasonably conclude they are being charged for 12,440 seats. That is a
 * support ticket, a trust problem, and in the worst case a churn event.
 *
 * So, non-negotiably (U-D3, U-D4):
 *   · this screen is its own SECTION, well away from licences and seats;
 *   · it never renders a seat count — not even for context;
 *   · every number is labelled with its unit;
 *   · the word "users" appears nowhere. Staff are staff or seats; customers
 *     are customers. "users" is banned here because it is the exact ambiguity
 *     being designed against.
 *
 * And per U-D5, this screen shows no price, implies no risk to access, and
 * offers no cleanup-to-save-money affordance. See allowanceMeter.tsx.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { useSession } from '../../context/sessionContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, DataTable, Tag, type Column } from '../../components/platform/primitives';
import { PButton, PSelect, PEmpty, PModal } from '../../components/platform/controls';
import { AllowanceMeter } from '../../components/platform/allowanceMeter';
import { FS, FW } from '../../utils/platformTokens';
import {
  getCustomerActivity,
  listCustomerAccessWithMeta,
  revokeCustomerAccess,
} from '../../services/platform/platformApi';
import type {
  CustomerAccessRow,
  CustomerActivityPeriodRow,
} from '../../schema/platform/types';

type StatusFilter = 'all' | CustomerAccessRow['status'];

const STATUS_TONE: Record<CustomerAccessRow['status'], 'success' | 'warn' | 'neutral' | 'danger'> = {
  active: 'success',
  suspended: 'warn',
  revoked: 'neutral',
  dormant: 'neutral',
};

function relative(iso: string | null): string {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

const CustomerAccessScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { org } = useSession();
  const toast = useToast();

  const [activity, setActivity] = useState<CustomerActivityPeriodRow | null>(null);
  const [rows, setRows] = useState<CustomerAccessRow[]>([]);
  /**
   * Why the access list is empty, when the server tells us.
   *
   * "Nothing to show" and "I cannot see this" are different facts, and on an
   * access surface they are very different facts — one means no customers have
   * signed in, the other means the console has no visibility. The server
   * distinguishes them and so does this screen.
   */
  const [listUnavailableReason, setListUnavailableReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const orgId = org?.id ?? null;

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [a, l] = await Promise.all([
        getCustomerActivity(orgId).catch(() => null),
        listCustomerAccessWithMeta(orgId).catch(() => ({ rows: [], reason: null })),
      ]);
      setActivity(a);
      setRows(l.rows);
      setListUnavailableReason(l.reason);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  /**
   * Status counts, mono numerals. Computed from the loaded set rather than
   * fetched separately so the counts and the list can never disagree — a
   * screen that says "4 suspended" above a list showing 3 is worse than one
   * that says nothing.
   */
  const counts = useMemo(() => {
    const base = { active: 0, suspended: 0, revoked: 0, dormant: 0 };
    for (const r of rows) base[r.status] += 1;
    return base;
  }, [rows]);

  /**
   * Default sort is OLDEST LAST-SEEN FIRST. This is the operator's primary
   * tool for finding stale access, so the useful order is the default rather
   * than something to be discovered.
   */
  const visible = useMemo(() => {
    const filtered = status === 'all' ? rows : rows.filter((r) => r.status === status);
    return [...filtered].sort((a, b) => {
      const av = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
      const bv = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
      return av - bv;
    });
  }, [rows, status]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const doRevoke = async () => {
    if (!orgId) return;
    setRevoking(true);
    try {
      const ids = [...selected];
      /**
       * NO OPTIMISTIC SUCCESS.
       *
       * Platform holds no customer identifier and cannot revoke a grant — it
       * answers 501 and says so. An earlier version of this handler swallowed
       * the error and reported success anyway, which is worse than the missing
       * feature: an operator who believes 900 grants were revoked and later
       * finds them live has been actively misled about a security action.
       *
       * So the failure is surfaced, the selection is KEPT (so the operator can
       * retry once the capability exists rather than re-selecting 900 rows),
       * and the dialog closes.
       */
      await revokeCustomerAccess(orgId, ids);
      toast.success(`Access revoked for ${ids.length} ${ids.length === 1 ? 'customer' : 'customers'}.`);
      setSelected(new Set());
      await load();
    } catch {
      toast.error(
        'Customer access is managed by the identity service, which is not yet available. Nothing was changed.',
      );
    } finally {
      setRevoking(false);
      setConfirming(false);
    }
  };

  const columns: Column<CustomerAccessRow>[] = [
    {
      key: 'sel',
      header: '',
      width: 40,
      render: (r) => (
        <Pressable
          onPress={() => toggle(r.id)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: selected.has(r.id) }}
          style={{
            width: 16, height: 16, borderRadius: 4, borderWidth: 1,
            borderColor: selected.has(r.id) ? c.accent : c.lineStrong,
            backgroundColor: selected.has(r.id) ? c.accent : 'transparent',
          }}
        />
      ),
    },
    {
      key: 'label',
      header: 'Customer',
      render: (r) => <Text style={{ color: c.ink, fontSize: FS.sm }}>{r.label}</Text>,
    },
    {
      key: 'status',
      header: 'Status',
      width: 130,
      render: (r) => <Tag dot tone={STATUS_TONE[r.status]}>{r.status}</Tag>,
    },
    {
      key: 'last_seen',
      header: 'Last seen',
      width: 150,
      render: (r) => (
        <Text style={{ color: c.ink3, fontSize: FS.sm, fontFamily: theme.font.mono }}>
          {relative(r.last_seen_at)}
        </Text>
      ),
    },
  ];

  const periodLabel = activity
    ? `${new Date(activity.period_start).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })} and ${new Date(activity.period_end).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })}`
    : 'the current period';

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 980, width: '100%', alignSelf: 'center' }}>
      {/*
        Its own heading, deliberately worded to describe ACCESS rather than
        headcount, and never adjacent to licences or seats (U-D4). The two
        live in different sections with different headings — "Licences and
        seats" is a separate screen entirely.
      */}
      <SettingsHeader
        title="Customer access"
        sub="The people who use your business as customers. Separate from your staff seats, and metered differently."
      />

      <Panel>
        {activity ? (
          <AllowanceMeter
            activeCount={activity.active_count}
            allowance={activity.allowance_at_period}
            blockSize={activity.block_size}
            blocksOver={activity.blocks_over_allowance}
            periodLabel={periodLabel}
          />
        ) : (
          <Text style={{ fontSize: FS.sm, color: c.ink3 }}>
            {loading
              ? 'Loading this period’s figure…'
              : 'No figure for this period yet. Counting begins at the start of your billing period.'}
          </Text>
        )}
      </Panel>

      <View style={{ height: 20 }} />

      {/*
        The access list — the operator's housekeeping surface.

        Framed as TIDINESS, never as cost. There is deliberately no line here
        suggesting that removing customers reduces a bill: the meter counts
        activity rather than records precisely so that deleting data saves
        nothing, and a UI that re-introduced the incentive would defeat the
        design it implements.
      */}
      <Panel
        title="Access"
        sub="Who can sign in to your business, and when they were last seen. Housekeeping only — removing access does not change your figure above."
      >
        {/* Status counts, mono numerals, above the list. */}
        <View style={{ flexDirection: 'row', gap: 20, marginBottom: 14, flexWrap: 'wrap' }}>
          {(['active', 'suspended', 'revoked', 'dormant'] as const).map((s) => (
            <View key={s}>
              <Text
                style={{
                  fontSize: 20, fontWeight: FW.semibold, color: c.ink,
                  fontFamily: theme.font.mono,
                }}
              >
                {counts[s].toLocaleString()}
              </Text>
              {/* Unit-labelled, per checklist #12. */}
              <Text style={{ fontSize: FS.xs, color: c.ink3 }}>{s} customers</Text>
            </View>
          ))}
        </View>

        <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <View style={{ width: 220 }}>
            <PSelect
              value={status}
              onChange={(v) => setStatus(v as StatusFilter)}
              options={[
                { value: 'all', label: 'All statuses' },
                { value: 'active', label: 'Active' },
                { value: 'suspended', label: 'Suspended' },
                { value: 'revoked', label: 'Revoked' },
                { value: 'dormant', label: 'Dormant' },
              ]}
            />
          </View>
          <Text style={{ fontSize: FS.xs, color: c.ink3 }}>
            Sorted by last seen, oldest first.
          </Text>
          <View style={{ flex: 1 }} />
          {selected.size > 0 && (
            <PButton kind="ghost" onPress={() => setConfirming(true)}>
              Revoke access ({selected.size})
            </PButton>
          )}
        </View>

        <DataTable
          columns={columns}
          rows={visible}
          keyExtractor={(r) => r.id}
          minWidth={640}
          empty={
            <PEmpty
              title={
                listUnavailableReason
                  ? 'Customer access is not shown here yet'
                  : 'No customer access recorded yet'
              }
              children={
                <Text style={{ fontSize: FS.sm, color: c.ink3, textAlign: 'center', maxWidth: 520, lineHeight: 19 }}>
                  {listUnavailableReason
                    ? 'Your customers sign in through the identity service, which keeps this list. It appears here once that service is live for your zone.'
                    : 'Customers appear here once they have signed in to your business.'}
                </Text>
              }
            />
          }
        />
      </Panel>

      {/*
        Bulk revoke confirmation. Two reassurances must appear, because an
        operator revoking hundreds of dormant grants needs certainty about what
        they have and have NOT destroyed — and because a rider who also uses
        another operator must be unaffected.
      */}
      <PModal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Revoke access for ${selected.size} ${selected.size === 1 ? 'customer' : 'customers'}?`}
        footer={
          <View style={{ flexDirection: 'row', gap: 10, justifyContent: 'flex-end' }}>
            <PButton kind="ghost" onPress={() => setConfirming(false)}>Cancel</PButton>
            <PButton kind="accent" loading={revoking} onPress={doRevoke}>
              Revoke {selected.size}
            </PButton>
          </View>
        }
      >
        <Text style={{ fontSize: FS.sm, color: c.ink2, lineHeight: 20 }}>
          They will need to sign in again to regain access to your business.
        </Text>
        <Text style={{ fontSize: FS.sm, color: c.ink2, lineHeight: 20, marginTop: 10 }}>
          This does not delete their records, bookings or invoice history, and does not affect
          their access at any other business.
        </Text>
      </PModal>
    </ScrollView>
  );
};

export default CustomerAccessScreen;
